import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  computed,
  input,
  output,
} from '@angular/core';
import { Popover, PopoverModule } from 'primeng/popover';

@Component({
  selector: 'gr-popover',
  imports: [PopoverModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: contents;' },
  template: `
    <p-popover
      #op
      [appendTo]="appendTo()"
      [styleClass]="panelClasses()"
      [dismissable]="dismissable()"
      (onShow)="onShow()"
      (onHide)="grHide.emit()"
    >
      <ng-content />
    </p-popover>
  `,
})
export class GrPopoverComponent {
  @ViewChild('op') popover!: Popover;

  readonly appendTo = input<'body' | HTMLElement | undefined>('body');
  readonly styleClass = input<string>('');
  readonly viewportPadding = input<number>(8);
  readonly dismissable = input<boolean>(true);

  protected readonly panelClasses = computed(() =>
    ['gr-popover-panel', this.styleClass()].filter(Boolean).join(' '),
  );

  readonly grHide = output<void>();

  readonly grShow = output<void>();

  // The panel is appended to body, so callers that need to reach into it (focus
  // management, roving keyboard navigation) can't query it from their own view.
  get panelElement(): HTMLElement | null {
    return (this.popover as unknown as { container?: HTMLElement }).container ?? null;
  }

  // `anchor` overrides the element the panel aligns to. Without it PrimeNG uses
  // the event's currentTarget, which means a keyboard shortcut (no event) can't
  // open the panel and a click on an inner element re-anchors it mid-flight.
  toggle(event: Event, anchor?: HTMLElement): void {
    this.popover.toggle(event, anchor);
  }

  show(event: Event | null, anchor?: HTMLElement): void {
    this.popover.show(event, anchor);
  }

  hide(): void {
    this.popover.hide();
  }

  align(): void {
    this.popover.align();
    this.clampToViewport();
  }

  setScrollDismissEnabled(enabled: boolean): void {
    if (enabled) {
      this.popover.bindScrollListener();
    } else {
      this.popover.unbindScrollListener();
    }
  }

  clampToViewport(): void {
    const container = this.panelElement;
    if (!container) return;
    const pad = this.viewportPadding();
    const rect = container.getBoundingClientRect();
    const vw = window.innerWidth;
    // PrimeNG aligns with logical insets. Writing a competing `left` value makes
    // subsequent alignments jump to the viewport edge instead of the trigger.
    const positionProperty = container.style.insetInlineStart
      ? 'insetInlineStart'
      : container.style.insetInlineEnd
        ? 'insetInlineEnd'
        : 'left';
    const currentLeft = parseFloat(container.style[positionProperty] || '0');
    let nextLeft = currentLeft;
    if (rect.right > vw - pad) {
      nextLeft = currentLeft - (rect.right - (vw - pad));
    }
    if (rect.left < pad) {
      nextLeft = currentLeft + (pad - rect.left);
    }
    if (nextLeft !== currentLeft) {
      container.style[positionProperty] = `${nextLeft}px`;
    }
  }

  protected onShow(): void {
    this.clampToViewport();
    this.grShow.emit();
  }
}
